import React, { useState } from "react";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Container,
  Paper,
  ThemeProvider,
  CssBaseline,
  Divider,
  Typography,
} from "@mui/material";
import axios from "axios";
import { SERVER } from "../../../../Constants";

import MainProfileSection from "./MainUserCard";
import ProfileDetailsSection from "./ProfileDetails";
import PreferenceDetailsSection from "./PreferencesDetails";
import StatDetailsSection from "./StatDetails";
import darkTheme from "../dashboardTheme";

const apiClient = axios.create({
  baseURL: SERVER,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

const ViewUserComponent = ({ user, backHandler, onStatusChange }) => {
  const profile = user?.profile || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const endpoint =
        newStatus === "active" ? "/user/unblockAccount" : "/user/blockAccount";
      await apiClient.post(endpoint, { userId: user._id });
      setSuccess(
        `Account successfully ${
          newStatus === "active" ? "unblocked" : "blocked"
        }`
      );
      onStatusChange?.(user._id, newStatus);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update account status"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          py: 5,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" mb={3} letterSpacing={0.3}>
            Profile Details
          </Typography>

          {/* Snackbar notifications */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          >
            <Alert severity="error">{error}</Alert>
          </Snackbar>

          <Snackbar
            open={!!success}
            autoHideDuration={4000}
            onClose={() => setSuccess(null)}
          >
            <Alert severity="success">{success}</Alert>
          </Snackbar>

          {/* Content Card */}
          <Box
            sx={{
              borderRadius: 3,
            }}
          >
            {/* Header / Back Button */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
              <Button
                variant="outlined"
                onClick={backHandler}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  borderColor: "rgba(199, 164, 93, 0.4)",
                  color: "#c7a45d",
                  "&:hover": {
                    borderColor: "#c7a45d",
                    backgroundColor: "rgba(199, 164, 93, 0.08)",
                  },
                }}
              >
                ← Back to Users
              </Button>
            </Box>

            <Divider
              sx={{
                mb: 3,
                borderColor: "rgba(199, 164, 93, 0.2)",
              }}
            />

            {/* Profile Sections */}
            <MainProfileSection
              user={user}
              profile={profile}
              loading={loading}
              handleStatusChange={handleStatusChange}
            />
            <Divider sx={{ my: 3, borderColor: "rgba(199, 164, 93, 0.1)" }} />
            <ProfileDetailsSection profile={profile} />
            <Divider sx={{ my: 3, borderColor: "rgba(199, 164, 93, 0.1)" }} />
            <PreferenceDetailsSection profile={profile} />
            <Divider sx={{ my: 3, borderColor: "rgba(199, 164, 93, 0.1)" }} />
            <StatDetailsSection profile={profile} />
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ViewUserComponent;
